import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import useModalScrollLock from '../../hooks/useModalScrollLock';
import SharedUserAvatar from '../UserAvatar';
import SlideToForfeit from './SlideToForfeit';

// Cartoon/arcade tokens — shared visual language with LiveBattleStoryViewer
// and the My Piks arcade cards: thick ink borders, hard offset shadows,
// flat colors, white sticker cards over a bold dotted panel. Theme-static
// (like the other match-flow surfaces) so it reads identically in light and
// dark mode — no useTheme(), no first-paint flash.
const INK = '#0a0a0a';
const CARD_BORDER = `2.5px solid ${INK}`;
const HARD_SHADOW = `4px 4px 0 ${INK}`;
const HARD_SHADOW_SM = `3px 3px 0 ${INK}`;
const DOTS = 'radial-gradient(rgba(255,255,255,0.12) 1.6px, transparent 1.6px)';
const YOU_BLUE = '#3b82f6';
const OPP_ORANGE = '#fb923c';
const ARCADE_FONT = 'Impact, "Arial Black", system-ui, -apple-system, sans-serif';

const MODE_META = {
  rush: {
    label: 'RUSH',
    icon: '⚡',
    color: '#fb923c',
    panel: '#f97316',
    tagline: '6 quick props from one live game',
    durationLabel: 'Live · ~3 min',
  },
  original: {
    label: 'ORIGINAL',
    icon: '🏆',
    color: '#3b82f6',
    panel: '#3b82f6',
    tagline: 'Highest balance after the games end wins',
    durationLabel: 'Ends after today',
  },
  tournament: {
    label: 'TOURNAMENT',
    icon: '👑',
    color: '#10b981',
    panel: '#0ea5a0',
    tagline: '3-day grind for a massive bankroll',
    durationLabel: '3-day battle',
  },
};

function modeMetaFor(n) {
  return MODE_META[n] || MODE_META.original;
}

function compactCoins(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(v % 1_000_000 ? 1 : 0)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(v % 1_000 ? 1 : 0)}K`;
  return String(v);
}

function fmtBalance(n, isBeta) {
  const v = Number(n);
  if (!Number.isFinite(v)) return null;
  return isBeta ? compactCoins(v) : `$${compactCoins(v)}`;
}

function PnlPill({ pnl }) {
  const v = Number(pnl);
  if (!Number.isFinite(v) || v === 0) {
    return (
      <span className="text-[9px] font-black" style={{ color: '#64748b', letterSpacing: '0.06em' }}>
        EVEN
      </span>
    );
  }
  const pos = v > 0;
  return (
    <span
      className="text-[9px] font-black"
      style={{ color: pos ? '#059669' : '#dc2626', letterSpacing: '0.06em' }}
    >
      {pos ? '+' : ''}{v.toFixed(1)}%
    </span>
  );
}

// One player column inside the white VS hero sticker.
function PlayerColumn({ user, tag, ring, balance, pnl, isBeta, crowned, anim }) {
  const label = fmtBalance(balance, isBeta);
  return (
    <div className={`flex flex-col items-center min-w-0 ${anim}`} style={{ flexBasis: 116, maxWidth: 116 }}>
      <span
        aria-hidden="true"
        style={{
          fontSize: 18,
          lineHeight: 1,
          marginBottom: 3,
          opacity: crowned ? 1 : 0,
          filter: `drop-shadow(0 1.5px 0 ${INK})`,
        }}
      >
        👑
      </span>
      <div
        className="rounded-full flex-shrink-0"
        style={{
          padding: 3.5,
          background: ring,
          border: CARD_BORDER,
          boxShadow: HARD_SHADOW_SM,
        }}
      >
        <div className="rounded-full overflow-hidden flex items-center justify-center" style={{ width: 62, height: 62, background: '#fff' }}>
          <SharedUserAvatar user={user} size={62} />
        </div>
      </div>
      <div
        className="mt-2 text-[10.5px] font-black uppercase truncate w-full text-center"
        style={{ color: INK, letterSpacing: '0.04em' }}
      >
        {user?.username || (tag === 'YOU' ? 'You' : 'Opponent')}
      </div>
      <div
        className="mt-1 text-[8.5px] font-black uppercase px-2.5 py-0.5 rounded-md"
        style={{
          background: ring,
          color: '#fff',
          border: `2px solid ${INK}`,
          boxShadow: `0 2px 0 ${INK}`,
          letterSpacing: '0.14em',
          textShadow: `0 1px 0 rgba(0,0,0,0.35)`,
        }}
      >
        {tag}
      </div>
      {label != null && (
        <div className="mt-1.5 flex flex-col items-center gap-0.5">
          <span className="text-[14px] font-black tabular-nums" style={{ color: INK }}>
            {label}
          </span>
          <PnlPill pnl={pnl} />
        </div>
      )}
    </div>
  );
}

export default function MyBattleOverviewModal({
  isOpen,
  onClose,
  matchup,
  opponent,
  myProfile,
  onOpenBattle,
  onForfeit,
  onMessageOpponent,
  onViewUpdates,
  myLiveBalance = null,
  opponentLiveBalance = null,
  myUnrealizedPnl = null,
  opponentUnrealizedPnl = null,
  isBeta = false,
}) {
  useModalScrollLock(isOpen);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (!isOpen) setClosing(false);
  }, [isOpen]);

  if (!isOpen || !matchup) return null;
  // Render through a portal to document.body so the fixed-position overlay
  // escapes any ancestor with a transform/filter/animation (e.g. the
  // LiveBattlesSection cartoon pop-in / HUD frames). Without this, the
  // transformed ancestor becomes the containing block for `position: fixed`,
  // trapping the modal inside that container on mobile — it fills the
  // container, pushes page content down, and scrolls within itself.
  if (typeof document === 'undefined') return null;

  const mode = modeMetaFor(matchup?.durationType || 'original');
  const isRush = matchup?.durationType === 'rush';
  const pot = (() => {
    const ps = parseFloat(matchup?.potSize);
    if (Number.isFinite(ps)) return ps;
    const sb = parseFloat(matchup?.startingBalance);
    if (Number.isFinite(sb)) return sb * 2;
    return null;
  })();
  const buyIn = (() => {
    const sb = parseFloat(matchup?.startingBalance);
    if (Number.isFinite(sb)) return sb;
    if (pot != null) return pot / 2;
    return null;
  })();

  const buyInLabel = buyIn != null
    ? (isBeta ? `${compactCoins(buyIn)} coins` : `$${compactCoins(buyIn)}`)
    : '—';

  // Crown the current leader (like the story viewer's 👑). With no live
  // balances yet (or a tie) nobody wears it.
  const myBal = Number(myLiveBalance);
  const oppBal = Number(opponentLiveBalance);
  const balancesKnown = Number.isFinite(myBal) && Number.isFinite(oppBal);
  const iLead = balancesKnown && myBal > oppBal;
  const oppLeads = balancesKnown && oppBal > myBal;

  const close = () => {
    if (closing) return;
    setClosing(true);
    setTimeout(() => onClose && onClose(), 150);
  };

  const handleOpen = () => {
    if (onOpenBattle) onOpenBattle(matchup);
    close();
  };

  const handleForfeit = () => {
    if (onForfeit) onForfeit(matchup);
    close();
  };

  const handleMessage = () => {
    if (onMessageOpponent) onMessageOpponent(opponent);
    close();
  };

  const handleViewUpdates = () => {
    if (onViewUpdates) onViewUpdates(matchup);
    close();
  };

  return createPortal((
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center p-3 mbom-fade-in"
      style={{ background: 'rgba(0,0,0,0.72)' }}
      onClick={close}
    >
      <div
        className="relative w-full max-w-md overflow-hidden mbom-pop-in"
        style={{
          background: mode.panel,
          border: CARD_BORDER,
          borderRadius: 24,
          boxShadow: `0 8px 0 ${INK}`,
          maxHeight: 'calc(100dvh - 24px)',
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mbom-title"
      >
        <style jsx>{`
          @keyframes mbomFadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes mbomPopIn {
            0% { transform: scale(0.86) translateY(14px); opacity: 0; }
            70% { transform: scale(1.03) translateY(0); opacity: 1; }
            100% { transform: scale(1) translateY(0); opacity: 1; }
          }
          @keyframes mbomSlamLeft {
            0% { transform: translateX(-26px) scale(0.9); opacity: 0; }
            100% { transform: translateX(0) scale(1); opacity: 1; }
          }
          @keyframes mbomSlamRight {
            0% { transform: translateX(26px) scale(0.9); opacity: 0; }
            100% { transform: translateX(0) scale(1); opacity: 1; }
          }
          @keyframes mbomRise {
            0% { transform: translateY(14px); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
          }
          @keyframes mbomCtaBob {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-3px); }
          }
          @keyframes mbomLiveDot {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.55; transform: scale(1.25); }
          }
          @keyframes mbomBolt {
            0%, 100% { transform: scale(1) rotate(0deg); }
            50% { transform: scale(0.9) rotate(-6deg); }
          }
          .mbom-fade-in { animation: mbomFadeIn 0.18s ease-out; }
          .mbom-pop-in { animation: mbomPopIn 0.42s cubic-bezier(0.34,1.56,0.64,1); }
          :global(.mbom-slam-left) { animation: mbomSlamLeft 0.45s cubic-bezier(0.34,1.56,0.64,1) both; }
          :global(.mbom-slam-right) { animation: mbomSlamRight 0.45s cubic-bezier(0.34,1.56,0.64,1) 0.1s both; }
          .mbom-rise { animation: mbomRise 0.4s cubic-bezier(0.22,1.4,0.4,1) backwards; }
          .mbom-cta { animation: mbomCtaBob 1.6s ease-in-out infinite; }
          .mbom-live-dot { animation: mbomLiveDot 1.2s ease-in-out infinite; }
          .mbom-bolt { animation: mbomBolt 1.1s ease-in-out infinite; display: inline-block; }
          .mbom-press:active {
            transform: translateY(2px);
            box-shadow: 0 1px 0 ${INK} !important;
          }
          @media (prefers-reduced-motion: reduce) {
            .mbom-fade-in, .mbom-pop-in, .mbom-rise, .mbom-cta,
            .mbom-live-dot, .mbom-bolt,
            :global(.mbom-slam-left), :global(.mbom-slam-right) {
              animation: none !important;
            }
          }
        `}</style>

        {/* Arcade dot texture over the flat mode-colored panel */}
        <div
          className="absolute inset-0 pointer-events-none opacity-60"
          style={{ backgroundImage: DOTS, backgroundSize: '16px 16px' }}
          aria-hidden="true"
        />

        <div className="relative overflow-y-auto" style={{ maxHeight: 'calc(100dvh - 24px)' }}>
          <div className="px-4 sm:px-5 pt-4 pb-5 flex flex-col gap-4">

            {/* Header — LIVE sticker + close sticker */}
            <div className="flex items-center justify-between">
              <div
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                style={{ background: '#fff', border: CARD_BORDER, boxShadow: `0 2.5px 0 ${INK}` }}
              >
                <span
                  className="inline-block rounded-full mbom-live-dot flex-shrink-0"
                  style={{ width: 8, height: 8, background: '#10b981', border: `1.5px solid ${INK}` }}
                  aria-hidden="true"
                />
                <span className="text-[9px] font-black uppercase" style={{ color: INK, letterSpacing: '0.2em' }}>
                  Live
                </span>
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={close}
                className="no-hover-effect mbom-press w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: '#fff', border: CARD_BORDER, boxShadow: `0 2.5px 0 ${INK}` }}
              >
                <svg className="w-4 h-4" style={{ color: INK }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Arcade title */}
            <div className="text-center">
              <div className="flex items-center justify-center gap-2">
                <span aria-hidden="true" className="mbom-bolt" style={{ fontSize: 24, lineHeight: 1, filter: `drop-shadow(0 2px 0 ${INK})` }}>⚡</span>
                <h2
                  id="mbom-title"
                  className="font-black uppercase"
                  style={{
                    color: '#facc15',
                    fontSize: 'clamp(26px, 7.5vw, 34px)',
                    lineHeight: 0.94,
                    letterSpacing: '0.02em',
                    fontStyle: 'italic',
                    WebkitTextStroke: `1.5px ${INK}`,
                    textShadow: `0 3px 0 ${INK}`,
                    margin: 0,
                    fontFamily: ARCADE_FONT,
                  }}
                >
                  You&apos;re In Battle!
                </h2>
                <span aria-hidden="true" className="mbom-bolt" style={{ fontSize: 24, lineHeight: 1, animationDelay: '0.2s', filter: `drop-shadow(0 2px 0 ${INK})` }}>⚡</span>
              </div>
              <p
                className="font-black uppercase mt-1.5"
                style={{ color: '#fff', fontSize: 10.5, letterSpacing: '0.22em', textShadow: `0 1.5px 0 ${INK}`, margin: 0, marginTop: 6 }}
              >
                vs {opponent?.username || 'Opponent'} · {mode.label}
              </p>
            </div>

            {/* VS hero — one white sticker holding the whole matchup */}
            <div
              className="mbom-rise px-2 pt-3 pb-3.5"
              style={{ background: '#fff', border: CARD_BORDER, borderRadius: 20, boxShadow: HARD_SHADOW, animationDelay: '80ms' }}
            >
              <div className="flex items-start justify-center gap-1.5 sm:gap-3">
                <PlayerColumn
                  user={myProfile}
                  tag="YOU"
                  ring={YOU_BLUE}
                  balance={myLiveBalance}
                  pnl={myUnrealizedPnl}
                  isBeta={isBeta}
                  crowned={iLead}
                  anim="mbom-slam-left"
                />
                <div className="self-center flex-shrink-0 px-0.5" style={{ paddingTop: 20 }}>
                  <span
                    className="font-black italic"
                    style={{
                      color: '#facc15',
                      fontSize: 30,
                      fontFamily: ARCADE_FONT,
                      WebkitTextStroke: `1.5px ${INK}`,
                      textShadow: `0 3px 0 ${INK}`,
                      letterSpacing: '0.04em',
                    }}
                  >
                    VS
                  </span>
                </div>
                <PlayerColumn
                  user={opponent ? { id: opponent.id, username: opponent.username, avatar: opponent.avatar } : null}
                  tag="OPP"
                  ring={OPP_ORANGE}
                  balance={opponentLiveBalance}
                  pnl={opponentUnrealizedPnl}
                  isBeta={isBeta}
                  crowned={oppLeads}
                  anim="mbom-slam-right"
                />
              </div>
            </div>

            {/* Mode sticker — what game you're playing + how long it lasts */}
            <div
              className="mbom-rise flex items-center gap-3 px-3 py-2.5"
              style={{ background: '#fff', border: CARD_BORDER, borderRadius: 16, boxShadow: HARD_SHADOW, animationDelay: '140ms' }}
            >
              <div
                className="flex items-center justify-center flex-shrink-0"
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 12,
                  background: mode.color,
                  border: CARD_BORDER,
                  boxShadow: `0 2.5px 0 ${INK}`,
                  fontSize: 21,
                }}
                aria-hidden="true"
              >
                {mode.icon}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[9.5px] font-black uppercase" style={{ color: mode.color, letterSpacing: '0.18em' }}>
                  {mode.label} MODE
                </div>
                <div className="text-[12px] font-bold mt-0.5 leading-snug" style={{ color: INK }}>
                  {mode.tagline}
                </div>
              </div>
              <div
                className="flex-shrink-0 px-2 py-1 rounded-lg text-[8.5px] font-black uppercase whitespace-nowrap"
                style={{
                  background: '#facc15',
                  color: INK,
                  border: `2px solid ${INK}`,
                  boxShadow: `0 2px 0 ${INK}`,
                  letterSpacing: '0.1em',
                }}
              >
                {mode.durationLabel}
              </div>
            </div>

            {/* Stakes — black sticker with yellow border, pot amount in white */}
            <div
              className="mbom-rise mx-auto w-full flex items-center justify-center gap-2 px-4 py-2.5"
              style={{
                background: INK,
                border: '2.5px solid #facc15',
                borderRadius: 999,
                boxShadow: '4px 4px 0 rgba(0,0,0,0.4)',
                maxWidth: 340,
                animationDelay: '200ms',
              }}
            >
              <span style={{ fontSize: 16 }} aria-hidden="true">🪙</span>
              <span className="font-black text-[10px] uppercase" style={{ letterSpacing: '0.14em', color: '#94a3b8' }}>
                Playing for
              </span>
              <span className="font-black text-[14px] uppercase tabular-nums" style={{ letterSpacing: '0.06em', color: '#ffffff' }}>
                {buyInLabel}
              </span>
            </div>

            {/* Quick actions — white sticker buttons */}
            <div className="grid grid-cols-2 gap-2.5">
              <button
                type="button"
                onClick={handleMessage}
                className="no-hover-effect mbom-press mbom-rise flex items-center justify-center gap-2 py-3 rounded-2xl font-black text-[11px] uppercase"
                style={{
                  background: '#fff',
                  border: CARD_BORDER,
                  boxShadow: `0 3px 0 ${INK}`,
                  color: INK,
                  letterSpacing: '0.08em',
                  animationDelay: '240ms',
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                </svg>
                <span className="truncate">Message</span>
              </button>
              <button
                type="button"
                onClick={handleViewUpdates}
                className="no-hover-effect mbom-press mbom-rise flex items-center justify-center gap-2 py-3 rounded-2xl font-black text-[11px] uppercase"
                style={{
                  background: '#fff',
                  border: CARD_BORDER,
                  boxShadow: `0 3px 0 ${INK}`,
                  color: INK,
                  letterSpacing: '0.08em',
                  animationDelay: '280ms',
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                <span className="truncate">Battle updates</span>
              </button>
            </div>

            {/* Rush keeps a primary CTA because it actually enters live gameplay.
                Original/Tournament battles are already "open" — you place picks on
                the dashboard — so a redundant "Open Battle" button is dropped. */}
            {isRush && (
              <button
                type="button"
                onClick={handleOpen}
                className="mbom-cta no-hover-effect w-full rounded-2xl font-black uppercase flex items-center justify-center gap-2 px-3 py-3"
                style={{
                  background: '#facc15',
                  border: CARD_BORDER,
                  color: INK,
                  letterSpacing: '0.06em',
                  boxShadow: `0 4px 0 ${INK}`,
                  fontFamily: ARCADE_FONT,
                }}
              >
                <span aria-hidden="true" style={{ fontSize: 17 }}>»</span>
                <span style={{ fontSize: 18, fontStyle: 'italic' }}>
                  Jump Into Rush
                </span>
                <span aria-hidden="true" style={{ fontSize: 17 }}>«</span>
              </button>
            )}

            {/* Forfeit — kept at the very bottom, visually quiet */}
            {onForfeit ? (
              <SlideToForfeit onConfirm={handleForfeit} />
            ) : (
              <button
                type="button"
                onClick={close}
                className="no-hover-effect mbom-press w-full py-3 rounded-2xl font-black text-[11px] uppercase"
                style={{
                  background: '#fff',
                  border: CARD_BORDER,
                  boxShadow: `0 3px 0 ${INK}`,
                  color: '#64748b',
                  letterSpacing: '0.12em',
                }}
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}
